"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import CommonDropdownItemsMobileWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsMobileWrapper";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { useCanShowDropCurationsAction } from "@/hooks/drops/useCanShowDropCurationsAction";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import type { FC } from "react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import WaveDropActionsAddReaction from "./WaveDropActionsAddReaction";
import WaveDropCurationsActionIcon from "./WaveDropCurationsActionIcon";
import WaveDropCurationsDialog from "./WaveDropCurationsDialog";
import WaveDropActionsMarkUnread from "./WaveDropActionsMarkUnread";
import WaveDropActionsRate from "./WaveDropActionsRate";
import WaveDropMobileMenuBoost from "./WaveDropMobileMenuBoost";
import WaveDropMobileMenuDelete from "./WaveDropMobileMenuDelete";
import WaveDropMobileMenuEdit from "./WaveDropMobileMenuEdit";
import WaveDropMobileMenuSetPinnedDrop from "./WaveDropMobileMenuSetPinnedDrop";
import WaveDropMobileMenuOpen from "./WaveDropMobileMenuOpen";
import WaveDropActionsQuickReact from "./WaveDropActionsQuickReact";
import { useWaveDropLayers } from "./WaveDropLayerContext";
import WaveDropMobileMenuCopyLink from "./WaveDropMobileMenuCopyLink";
interface WaveDropMobileMenuProps {
  readonly drop: ApiDrop;
  readonly isOpen: boolean;
  readonly showReplyAndQuote: boolean;
  readonly longPressTriggered: boolean;
  readonly setOpen: (open: boolean) => void;
  readonly onReply: () => void;
  readonly onAddReaction: () => void;
  readonly onEdit?: (() => void) | undefined;
  readonly onBoostAnimation?: (() => void) | undefined;
  readonly showOpenOption?: boolean | undefined;
  readonly showCopyOption?: boolean | undefined;
  readonly showVoting?: boolean | undefined;
}
type TimeoutRef = {
  current: ReturnType<typeof setTimeout> | null;
};
const clearCurationsDialogTimeout = (timeoutRef: TimeoutRef) => {
  if (timeoutRef.current !== null) {
    globalThis.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }
};
const openCurationsDialogAfterMenuClose = ({
  timeoutRef,
  closeMenu,
  setIsCurationsDialogOpen
}: {
  readonly timeoutRef: TimeoutRef;
  readonly closeMenu: () => void;
  readonly setIsCurationsDialogOpen: (open: boolean) => void;
}) => {
  closeMenu();
  clearCurationsDialogTimeout(timeoutRef);
  timeoutRef.current = globalThis.setTimeout(() => {
    setIsCurationsDialogOpen(true);
    timeoutRef.current = null;
  }, 250);
};
function WaveDropMobileMenuAuthenticatedActions(t0) {
  const $ = _c(61);
  const {
    extendedDrop,
    drop,
    showReplyAndQuote,
    isTemporaryDrop,
    onReply,
    onAddReaction,
    dialogZIndexClassName,
    onBoostAnimation,
    showOpenOption,
    showCopyOption,
    showCurationsAction,
    onCurationsClick,
    isAuthor,
    showOptions,
    onEdit,
    canSetPinnedDrop,
    canDelete,
    closeMenu,
    showVoting
  } = t0;
  let t1;
  if ($[0] !== closeMenu || $[1] !== extendedDrop) {
    t1 = <WaveDropActionsQuickReact drop={extendedDrop} isMobile={true} onReacted={closeMenu} />;
    $[0] = closeMenu;
    $[1] = extendedDrop;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== dialogZIndexClassName || $[4] !== extendedDrop || $[5] !== onAddReaction) {
    t2 = <WaveDropActionsAddReaction drop={extendedDrop} isMobile={true} onAddReaction={onAddReaction} dialogZIndexClassName={dialogZIndexClassName} />;
    $[3] = dialogZIndexClassName;
    $[4] = extendedDrop;
    $[5] = onAddReaction;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== closeMenu || $[8] !== isTemporaryDrop || $[9] !== onReply || $[10] !== showReplyAndQuote) {
    t3 = showReplyAndQuote && <button className={`tw-flex tw-items-center tw-gap-x-4 tw-rounded-xl tw-border-0 tw-bg-iron-950 tw-p-4 ${isTemporaryDrop ? "tw-cursor-default tw-opacity-50" : "active:tw-bg-iron-800"} tw-transition-colors tw-duration-200`} onClick={() => {
      closeMenu();
      globalThis.setTimeout(() => onReply(), 250);
    }} disabled={isTemporaryDrop}><svg className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-300" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M7.49 12L3.74 8.248m0 0l3.75-3.75m-3.75 3.75h16.5V19.5" /></svg><span className="tw-text-base tw-font-semibold tw-text-iron-300">Reply</span></button>;
    $[7] = closeMenu;
    $[8] = isTemporaryDrop;
    $[9] = onReply;
    $[10] = showReplyAndQuote;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== closeMenu || $[13] !== extendedDrop || $[14] !== onBoostAnimation) {
    t4 = <WaveDropMobileMenuBoost drop={extendedDrop} onBoostChange={closeMenu} onBoostAnimation={onBoostAnimation} />;
    $[12] = closeMenu;
    $[13] = extendedDrop;
    $[14] = onBoostAnimation;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== closeMenu || $[17] !== drop || $[18] !== showOpenOption) {
    t5 = showOpenOption && <WaveDropMobileMenuOpen drop={{
      type: DropSize.FULL,
      ...drop,
      stableHash: drop.id,
      stableKey: drop.id
    }} onOpenChange={closeMenu} />;
    $[16] = closeMenu;
    $[17] = drop;
    $[18] = showOpenOption;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== closeMenu || $[21] !== drop || $[22] !== showCopyOption) {
    t6 = showCopyOption && <WaveDropMobileMenuCopyLink drop={drop} onCopy={closeMenu} />;
    $[20] = closeMenu;
    $[21] = drop;
    $[22] = showCopyOption;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== onCurationsClick || $[25] !== showCurationsAction) {
    t7 = showCurationsAction && <button type="button" onClick={onCurationsClick} className="tw-flex tw-items-center tw-gap-x-4 tw-rounded-xl tw-border-0 tw-bg-iron-950 tw-p-4 tw-text-left tw-transition-colors tw-duration-200 active:tw-bg-iron-800"><WaveDropCurationsActionIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-300" /><span className="tw-text-base tw-font-semibold tw-text-iron-300">Curate</span></button>;
    $[24] = onCurationsClick;
    $[25] = showCurationsAction;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== closeMenu || $[28] !== drop) {
    t8 = <WaveDropActionsMarkUnread drop={drop} isMobile={true} onMarkUnread={closeMenu} />;
    $[27] = closeMenu;
    $[28] = drop;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== closeMenu || $[31] !== drop || $[32] !== isAuthor || $[33] !== showVoting) {
    t9 = showVoting && !isAuthor && <WaveDropActionsRate drop={drop} isMobile={true} onRated={closeMenu} />;
    $[30] = closeMenu;
    $[31] = drop;
    $[32] = isAuthor;
    $[33] = showVoting;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== closeMenu || $[36] !== drop || $[37] !== onEdit || $[38] !== showOptions) {
    t10 = showOptions && onEdit && drop.drop_type !== ApiDropType.Participatory && <WaveDropMobileMenuEdit drop={drop} onEdit={onEdit} onEditTriggered={closeMenu} />;
    $[35] = closeMenu;
    $[36] = drop;
    $[37] = onEdit;
    $[38] = showOptions;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== canSetPinnedDrop || $[41] !== closeMenu || $[42] !== drop) {
    t11 = canSetPinnedDrop && <WaveDropMobileMenuSetPinnedDrop drop={drop} onPinnedDropSet={closeMenu} />;
    $[40] = canSetPinnedDrop;
    $[41] = closeMenu;
    $[42] = drop;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== canDelete || $[45] !== closeMenu || $[46] !== drop) {
    t12 = canDelete && <WaveDropMobileMenuDelete drop={drop} onDropDeleted={closeMenu} />;
    $[44] = canDelete;
    $[45] = closeMenu;
    $[46] = drop;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== t1 || $[49] !== t10 || $[50] !== t11 || $[51] !== t12 || $[52] !== t2 || $[53] !== t3 || $[54] !== t4 || $[55] !== t5 || $[56] !== t6 || $[57] !== t7 || $[58] !== t8 || $[59] !== t9) {
    t13 = <>{t1}{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}</>;
    $[48] = t1;
    $[49] = t10;
    $[50] = t11;
    $[51] = t12;
    $[52] = t2;
    $[53] = t3;
    $[54] = t4;
    $[55] = t5;
    $[56] = t6;
    $[57] = t7;
    $[58] = t8;
    $[59] = t9;
    $[60] = t13;
  } else {
    t13 = $[60];
  }
  return t13;
}
const WaveDropMobileMenu: FC<WaveDropMobileMenuProps> = t0 => {
  const $ = _c(57);
  const {
    drop,
    isOpen,
    showReplyAndQuote,
    longPressTriggered,
    setOpen,
    onReply,
    onAddReaction,
    onEdit,
    onBoostAnimation,
    showOpenOption: t1,
    showCopyOption: t2,
    showVoting: t3
  } = t0;
  const showOpenOption = t1 === undefined ? true : t1;
  const showCopyOption = t2 === undefined ? true : t2;
  const showVoting = t3 === undefined ? true : t3;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  let t4;
  if ($[0] !== drop.id) {
    t4 = drop.id.startsWith("temp-");
    $[0] = drop.id;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const isTemporaryDrop = t4;
  const {
    canDelete,
    canSetPinnedDrop
  } = useDropInteractionRules(drop);
  const {
    mobileMenuZIndexClassName,
    mobileDialogZIndexClassName
  } = useWaveDropLayers();
  let t5;
  if ($[2] !== drop) {
    t5 = {
      ...drop,
      type: DropSize.FULL,
      stableKey: drop.id,
      stableHash: drop.id
    };
    $[2] = drop;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const extendedDrop = t5;
  const [isCurationsDialogOpen, setIsCurationsDialogOpen] = useState(false);
  const curationsDialogTimeoutRef = useRef(null);
  let t6;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      clearCurationsDialogTimeout(curationsDialogTimeoutRef);
    };
    t7 = [];
    $[4] = t6;
    $[5] = t7;
  } else {
    t6 = $[4];
    t7 = $[5];
  }
  useEffect(t6, t7);
  const isAuthor = connectedProfile?.handle === drop.author.handle && !activeProfileProxy;
  const connectedProfileHandle = connectedProfile?.handle ?? null;
  let t8;
  if ($[6] !== activeProfileProxy || $[7] !== connectedProfileHandle || $[8] !== drop.author.handle || $[9] !== drop.id) {
    t8 = connectedProfileHandle !== null && !activeProfileProxy && !drop.id.startsWith("temp-") && connectedProfileHandle === drop.author.handle;
    $[6] = activeProfileProxy;
    $[7] = connectedProfileHandle;
    $[8] = drop.author.handle;
    $[9] = drop.id;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const showOptions = t8;
  let t9;
  if ($[11] !== setOpen) {
    t9 = () => setOpen(false);
    $[11] = setOpen;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const closeMenu = t9;
  const showGuestCopyOnly = connectedProfileHandle === null;
  const t10 = drop.wave.authenticated_user_admin === true;
  const t11 = (isOpen || isCurationsDialogOpen) && connectedProfileHandle !== null;
  let t12;
  if ($[13] !== drop.id || $[14] !== isTemporaryDrop || $[15] !== t10 || $[16] !== t11) {
    t12 = {
      dropId: drop.id,
      isTemporaryDrop,
      isWaveAdmin: t10,
      enabled: t11
    };
    $[13] = drop.id;
    $[14] = isTemporaryDrop;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const showCurationsAction = useCanShowDropCurationsAction(t12);
  let t13;
  if ($[18] !== closeMenu) {
    t13 = () => openCurationsDialogAfterMenuClose({
      timeoutRef: curationsDialogTimeoutRef,
      closeMenu,
      setIsCurationsDialogOpen
    });
    $[18] = closeMenu;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const handleCurationsClick = t13;
  const t14 = `tw-grid tw-grid-cols-1 ${longPressTriggered && "tw-select-none"}`;
  let t15;
  if ($[20] !== canDelete || $[21] !== canSetPinnedDrop || $[22] !== closeMenu || $[23] !== drop || $[24] !== extendedDrop || $[25] !== handleCurationsClick || $[26] !== isAuthor || $[27] !== isTemporaryDrop || $[28] !== mobileDialogZIndexClassName || $[29] !== onAddReaction || $[30] !== onBoostAnimation || $[31] !== onEdit || $[32] !== onReply || $[33] !== showCopyOption || $[34] !== showCurationsAction || $[35] !== showGuestCopyOnly || $[36] !== showOpenOption || $[37] !== showOptions || $[38] !== showReplyAndQuote || $[39] !== showVoting) {
    t15 = showGuestCopyOnly ? showCopyOption && <WaveDropMobileMenuCopyLink drop={drop} onCopy={closeMenu} /> : <WaveDropMobileMenuAuthenticatedActions extendedDrop={extendedDrop} drop={drop} showReplyAndQuote={showReplyAndQuote} isTemporaryDrop={isTemporaryDrop} onReply={onReply} onAddReaction={onAddReaction} dialogZIndexClassName={mobileDialogZIndexClassName} onBoostAnimation={onBoostAnimation} showOpenOption={showOpenOption} showCopyOption={showCopyOption} showCurationsAction={showCurationsAction} onCurationsClick={handleCurationsClick} isAuthor={isAuthor} showOptions={showOptions} onEdit={onEdit} canSetPinnedDrop={canSetPinnedDrop} canDelete={canDelete} closeMenu={closeMenu} showVoting={showVoting} />;
    $[20] = canDelete;
    $[21] = canSetPinnedDrop;
    $[22] = closeMenu;
    $[23] = drop;
    $[24] = extendedDrop;
    $[25] = handleCurationsClick;
    $[26] = isAuthor;
    $[27] = isTemporaryDrop;
    $[28] = mobileDialogZIndexClassName;
    $[29] = onAddReaction;
    $[30] = onBoostAnimation;
    $[31] = onEdit;
    $[32] = onReply;
    $[33] = showCopyOption;
    $[34] = showCurationsAction;
    $[35] = showGuestCopyOnly;
    $[36] = showOpenOption;
    $[37] = showOptions;
    $[38] = showReplyAndQuote;
    $[39] = showVoting;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t14 || $[42] !== t15) {
    t16 = <div className={t14}>{t15}</div>;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== isOpen || $[45] !== mobileMenuZIndexClassName || $[46] !== setOpen || $[47] !== t16) {
    t17 = createPortal(<CommonDropdownItemsMobileWrapper isOpen={isOpen} setOpen={setOpen} zIndexClassName={mobileMenuZIndexClassName}>{t16}</CommonDropdownItemsMobileWrapper>, document.body);
    $[44] = isOpen;
    $[45] = mobileMenuZIndexClassName;
    $[46] = setOpen;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== drop.id || $[50] !== drop.wave || $[51] !== isCurationsDialogOpen || $[52] !== showCurationsAction) {
    t18 = showCurationsAction && <WaveDropCurationsDialog dropId={drop.id} wave={drop.wave} isOpen={isCurationsDialogOpen} onClose={() => setIsCurationsDialogOpen(false)} />;
    $[49] = drop.id;
    $[50] = drop.wave;
    $[51] = isCurationsDialogOpen;
    $[52] = showCurationsAction;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== t17 || $[55] !== t18) {
    t19 = <>{t17}{t18}</>;
    $[54] = t17;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  return t19;
};
export default WaveDropMobileMenu;